// Create file: src/components/LimitsDisplay.tsx - SIMPLE VERSION

import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Mail, ChevronDown } from 'lucide-react';
import logger from '../../utils/logger';

interface DailyMailTracker {
  type: string;
  isTodayLimitReached: boolean;
  allowedLimit: number;
  todayRemainingLimit: number;
}

interface LimitsDisplayProps {
  dailyMailTracker: DailyMailTracker[];
  whatsappLimit: string;
}

const LimitsDisplay: React.FC<LimitsDisplayProps> = ({ dailyMailTracker, whatsappLimit }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  logger.log('🚀 LimitsDisplay - WhatsApp Limit:', whatsappLimit);
  logger.log('🚀 LimitsDisplay - Email Data:', dailyMailTracker);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Calculate totals
  const totalServices = 1 + (dailyMailTracker?.length || 0); // 1 for WhatsApp + email services
  const totalEmailRemaining = dailyMailTracker?.reduce((sum: number, tracker: DailyMailTracker) => 
    sum + tracker.todayRemainingLimit, 0) || 0;
  const totalEmailLimit = dailyMailTracker?.reduce((sum: number, tracker: DailyMailTracker) => 
    sum + tracker.allowedLimit, 0) || 0;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', marginRight: '12px' }}>
      {/* Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
        }}
      >
        <MessageCircle size={16} />
        <Mail size={16} />
        <span>Service Limits</span>
        <div style={{
          background: 'rgba(255,255,255,0.2)',
          padding: '2px 6px',
          borderRadius: '10px',
          fontSize: '11px'
        }}>
          {totalServices}
        </div>
        <ChevronDown size={14} style={{ 
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: '0',
          marginTop: '8px',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid #e5e7eb',
          minWidth: '300px',
          zIndex: 1000,
          animation: 'slideDown 0.2s ease'
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f3f4f6',
            background: '#f9fafb',
            borderRadius: '8px 8px 0 0'
          }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              📊 Daily Service Limits
            </h4>
          </div>

          <div style={{ padding: '12px 16px' }}>
            {/* WhatsApp Section */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ 
                fontSize: '12px', 
                fontWeight: '600', 
                color: '#6b7280',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                WhatsApp Messaging
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                background: '#f0fdf4',
                borderRadius: '6px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img alt="WhatsApp" src="assets/img/whatsapp.png" style={{ width: 18, height: 18 }} />
                  <span style={{ fontSize: '13px', color: '#374151' }}>New Conversations</span>
                </div>
                <div style={{
                  background: '#10b981',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {whatsappLimit} / 24h
                </div>
              </div>
            </div>

            {/* Email Section */}
            <div>
              <div style={{ 
                fontSize: '12px', 
                fontWeight: '600', 
                color: '#6b7280',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Email Services
              </div>

              {dailyMailTracker && dailyMailTracker.length > 0 ? (
                <>
                  {dailyMailTracker.map((tracker: DailyMailTracker) => {
                    const isLow = tracker.todayRemainingLimit < tracker.allowedLimit * 0.2;
                    const isEmpty = tracker.isTodayLimitReached || tracker.todayRemainingLimit === 0;
                    
                    return (
                      <div
                        key={tracker.type}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: isEmpty ? '#fef2f2' : isLow ? '#fffbeb' : '#f0f9ff',
                          borderRadius: '6px',
                          border: `1px solid ${isEmpty ? '#fecaca' : isLow ? '#fed7aa' : '#bae6fd'}`,
                          marginBottom: '6px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {tracker.type === 'gmail' && <img alt="Gmail" src="assets/img/gmail.png" style={{ width: 18, height: 18 }} />}
                          {tracker.type === 'brevo' && <img alt="Brevo" src="assets/img/brevo.png" style={{ width: 18, height: 18 }} />}
                          {tracker.type === 'zeptomail' && <img alt="Zeptomail" src="assets/img/email.png" style={{ width: 18, height: 18 }} />}
                          <span style={{ fontSize: '13px', color: '#374151', textTransform: 'capitalize' }}>
                            {tracker.type}
                          </span>
                        </div>
                        <div style={{
                          background: isEmpty ? '#ef4444' : isLow ? '#f59e0b' : '#0ea5e9',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {tracker.todayRemainingLimit}/{tracker.allowedLimit}
                        </div>
                      </div>
                    );
                  })}

                  {/* Total */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: '#f3f4f6',
                    borderRadius: '6px',
                    border: '2px solid #d1d5db',
                    marginTop: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        background: '#6366f1',
                        color: 'white',
                        width: '18px',
                        height: '18px',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>
                        Σ
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        Total Email
                      </span>
                    </div>
                    <div style={{
                      background: '#374151',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {totalEmailRemaining}/{totalEmailLimit}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontSize: '13px',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px dashed #d1d5db'
                }}>
                  No email services configured
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default LimitsDisplay;